import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGitStatus } from "../../store/gitStatusSlice";
import {
  forceReloadIFrame,
  sendMessageToIFrame,
  setIFrameChanged,
  setPendingSelectionCodebaseId,
} from "../../store/iframeSlice";
import { toast } from "sonner";
import axios from "axios";
import {
  getAxiosErorrMessage,
  getLocalStorageCompressed,
  getUnexpiredToken,
  setLocalStorageCompressed,
  useActiveCanvas,
  useActiveProject,
  usePrevious,
} from "@/utils/utils";
import {
  resolveCurrentLedgerChain,
  useChangeLedgerStore,
} from "@/store/zustand/changeLedger";
import {
  AnyChangeLedgerItem,
  CHANGE_TYPES_WITH_INSTANT_UNDO,
  ChangeType,
  RedoChange,
  RedoChangeFields,
  UndoChange,
  UndoChangeFields,
  UnknownChange,
  reconstructChangeLedgerClass,
} from "@/store/zustand/changeLedgerTypes";
import { useSupabase } from "./DatabaseSyncWrapper";
import { getTreeElementLookupToSend } from "@/utils/iframeUtils";
import { useLoadCodebase } from "@/utils/devServerUtils";
import { ActivityChangeTypeToName } from "../ActivityStream/ActivityStreamItem";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { fetchFileTree } from "@/store/codebaseSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useParams } from "react-router";
import { useAuth } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";
interface Props {}

const getDisplayNameForItem = (
  ledgerItem: AnyChangeLedgerItem,
  activities: any
) => {
  if (!ledgerItem) {
    return "";
  }

  if (ledgerItem.type === ChangeType.UNKNOWN) {
    const matchingActivity = activities.find(
      (activity) => activity.id === ledgerItem.activityId
    );
    if (matchingActivity) {
      return ActivityChangeTypeToName[matchingActivity.change_type];
    }
  }

  return ledgerItem.changeName;
};

const ChangeLedgerHelper: React.FC<Props> = React.memo(() => {
  const { signOut } = useAuth();

  const params = useParams();
  // @ts-expect-error TS2322
  const canvasId: string = params.canvasId;

  const canvas = useActiveCanvas();

  const storyboardToNavTree = useCanvasStore(
    (store) => store.storyboardToNavTree
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  // To get instant updates on the tree elements after an API change
  const treeElementLookupRef = useRef(treeElementLookup);

  useEffect(() => {
    treeElementLookupRef.current = treeElementLookup;
  }, [treeElementLookup]);

  const dispatch = useDispatch<any>();

  // Note - this causes a re-render on every state change, but ok here because all variables
  // are relevant to this component
  const {
    ledger,
    ledgerCanvasId,
    flushing,
    triggerFlush,
    setTriggerFlush,
    clearLedger,
    setLedger,
    updateChange,
    setFlushing,
    ongoingChange,
  } = useChangeLedgerStore();
  const [flushingStartedTs, setFlushingStartedTs] = useState(null);
  const [lastChangeTs, setLastChangeTs] = useState(null);
  const [checkTrigger, setCheckTrigger] = useState(0);

  const triggeredResetRef = useRef(false);

  const activities = useSelector(
    (state: any) => state.supabaseStore.activityStreamList
  );

  const changeBeingProcessed = useChangeLedgerStore(
    (state) => state.changeBeingProcessed
  );
  const setChangeBeingProcessed = useChangeLedgerStore(
    (state) => state.setChangeBeingProcessed
  );

  const changeIsBeingProcessedRef = useRef(false);

  /**
   * @param changeBeingProcessed Explicitly set the ref and the store instead
   * of waiting for a use effect to be called so the ref is updated immediately
   */
  const setInternalChangeBeingProcessed = (changeBeingProcessed: boolean) => {
    setChangeBeingProcessed(changeBeingProcessed);
    changeIsBeingProcessedRef.current = changeBeingProcessed;
  };

  const { supabaseClient, triggerRefetchActivityStream } = useSupabase();

  const loadCodebase = useLoadCodebase();

  const [flushDelay, setFlushDelay] = useState(0);

  const { session } = useSession();

  useEffect(() => {
    const getFlags = async () => {
      if (!supabaseClient) {
        return;
      }

      try {
        const readQuery = await supabaseClient
          .from("global_flags")
          .select("*")
          .eq("id", 1)
          .single();
        // @ts-expect-error TS18047
        setFlushDelay(readQuery.data.flush_delay || 0);
      } catch (e) {}
    };

    getFlags();
  }, [supabaseClient]);

  /**
   * Clean ledger every time you load a project
   */
  useEffect(() => {
    if (canvasId && ledgerCanvasId !== canvasId) {
      const parsedLedger = getLocalStorageCompressed(`ledger-${canvasId}`);
      if (parsedLedger) {
        try {
          const filteredLedger = parsedLedger
            .map((plainJsChange) => reconstructChangeLedgerClass(plainJsChange))
            .filter(
              // Filter out any items that are unconsumed
              (item: AnyChangeLedgerItem) => !item.needToProcessChange()
            );
          setLedger(filteredLedger, canvasId);
        } catch (e) {
          console.error(e);
        }
      } else {
        clearLedger(canvasId);
      }

      setFlushing(false);

      // Every new project loaded, apply a flush,
      // giving time for unflushed changes to load
      setTimeout(() => {
        setTriggerFlush(true);
      }, 500);
    }
  }, [canvasId]);

  /**
   * Send the flushing state to the iframe
   */
  useEffect(() => {
    dispatch(
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "setIsFlushing",
          args: [flushing],
        },
      })
    );
  }, [flushing]);

  /**
   * Persist the ledger to local storage
   */
  useEffect(() => {
    if (canvasId) {
      try {
        // Sometimes the ledger can be too big, just don't store it
        setLocalStorageCompressed(`ledger-${canvasId}`, ledger);
      } catch (e) {
        console.error(e);
      }
    }
  }, [ledger]);

  /**
   * When the ledger gets out of sync with the activity stream, re-create the ledger from
   * the activity stream
   */
  const resetLedgerToActivityStream = () => {
    // Unconsumed items do not need to be reset as they are not tied to activities yet
    const unconsumedLedger = ledger.filter((ledgerItem) => {
      return ledgerItem.needToProcessChange();
    });

    // Activities are sorted from most recent to least recent, while the change ledger is sorted
    // in the opposite direction

    let newLedger = [];
    let allUndos = [];
    let latestAppliedChange = null;
    // Missing activities are sorted from most recent to least recent
    activities.forEach((activity) => {
      const unknownChange = new UnknownChange({});
      unknownChange.activityId = activity.id;

      if (activity.change_undone) {
        const undoChange = new UndoChange({
          changeToUndo: unknownChange,
        });
        undoChange.markProcessedSucceeded();
        undoChange.doNotSendInstantUpdate();

        // @ts-expect-error TS2322
        newLedger = [unknownChange, ...newLedger];
        // @ts-expect-error TS2345
        allUndos.push(undoChange);
      } else {
        // @ts-expect-error TS2322
        newLedger = [unknownChange, ...newLedger];
        if (!latestAppliedChange) {
          // @ts-expect-error TS2322
          latestAppliedChange = unknownChange;
        }
      }
    });

    newLedger = [...newLedger, ...allUndos];

    // @ts-expect-error TS2345
    let indexInLedger = newLedger.indexOf(latestAppliedChange);

    // Remap any unconsumed UNDO/REDO events to the expected events
    unconsumedLedger.forEach((ledgerItem) => {
      if (ledgerItem.type === ChangeType.UNDO && indexInLedger >= 0) {
        ledgerItem.changeFields.changeToUndo = newLedger[indexInLedger];
        indexInLedger -= 1;
        // @ts-expect-error TS2345
        newLedger.push(ledgerItem);
        return;
      }

      if (
        ledgerItem.type === ChangeType.REDO &&
        indexInLedger < newLedger.length - 1
      ) {
        ledgerItem.changeFields.changeToRedo = newLedger[indexInLedger + 1];
        indexInLedger += 1;
        // @ts-expect-error TS2345
        newLedger.push(ledgerItem);
        return;
      }

      // @ts-expect-error TS2345
      newLedger.push(ledgerItem);
      indexInLedger += 1;
    });

    try {
      setLedger(newLedger, canvasId);
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Clear the ledger of any items that are no longer in the activity stream.
   *
   * Also, add items that are in the activity stream but not in the ledger as unknown items
   * so that they can be undone
   */
  useEffect(() => {
    // Don't apply this logic when a change is being processed as an acticity may not have yet
    // been attached to a valid change
    if (changeBeingProcessed) {
      return;
    }

    if (!activities?.length) {
      return;
    }

    const activitiesCanvasId = activities[0].canvas_id;
    if (
      activitiesCanvasId !== canvasId ||
      activitiesCanvasId !== ledgerCanvasId
    ) {
      return;
    }

    const {
      resolvedLedgerChain, // These are the ledger items without undo/redo in them
    } = resolveCurrentLedgerChain(ledger);

    const consumedLedger = resolvedLedgerChain.filter(
      (ledgerItem) => !ledgerItem.needToProcessChange()
    );

    const activitiesWithoutUndone = activities.filter(
      (activity) => !activity.change_undone
    );

    // This is the case where there is a new activity that shows up that this client doesn't know about
    // (another user has made a change, or a change not in the ledger was made)
    const newUnknownActivity =
      consumedLedger?.length &&
      activitiesWithoutUndone?.length &&
      consumedLedger[consumedLedger.length - 1].activityId !==
        activitiesWithoutUndone[0].id &&
      consumedLedger[consumedLedger.length - 1].activityId ===
        activitiesWithoutUndone[1]?.id;

    const currentActivityDoesNotMatch =
      activitiesWithoutUndone?.length &&
      activitiesWithoutUndone[0].id !== consumedLedger?.[0]?.activityId;

    const baseOfLedgerDoesNotMatch =
      consumedLedger?.length &&
      activitiesWithoutUndone?.length &&
      consumedLedger[0].activityId !==
        activitiesWithoutUndone[activitiesWithoutUndone.length - 1].id;

    if (
      (!consumedLedger?.length && activitiesWithoutUndone.length) ||
      newUnknownActivity ||
      baseOfLedgerDoesNotMatch ||
      currentActivityDoesNotMatch
    ) {
      if (triggeredResetRef.current) {
        return;
      }

      triggeredResetRef.current = true;

      resetLedgerToActivityStream();
    }
  }, [ledger, activities, changeBeingProcessed]);

  // Allow resets again after activity changes
  useEffect(() => {
    triggeredResetRef.current = false;
  }, [activities]);

  const prevCanvasId = usePrevious(canvasId);
  // @ts-expect-error TS2322
  const prevActivities: any[] = usePrevious(activities);

  /**
   * If the activity stream was deleted (project ID hasn't changed but the activities went to an empty list)
   * clear the change ledger as well
   */
  useEffect(() => {
    if (
      prevCanvasId === canvasId &&
      prevActivities?.length &&
      !activities?.length
    ) {
      clearLedger(canvasId);
    }
  }, [activities]);

  /**
   * Update the check trigger once a second for the polling checks
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setCheckTrigger((prev) => prev + 1);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  /**
   * If there are any activities that are in the FLUSHING state, set flushing to true
   */
  useEffect(() => {
    if (
      activities?.some((activity: any) => activity.flush_status === "FLUSHING")
    ) {
      if (!flushingStartedTs) {
        // @ts-expect-error TS2345
        setFlushingStartedTs(new Date());
      }
      setFlushing(true);
    } else {
      setFlushingStartedTs(null);
      setFlushing(false);
    }
  }, [activities]);

  /**
   * Mark all activities as flushed
   */
  const updateFlushed = async () => {
    // @ts-expect-error
    await supabaseClient
      .from("activity_stream")
      .update({
        flush_status: "FLUSHED",
      })
      .eq("flush_status", "FLUSHING")
      .eq("canvas_id", canvasId);
  };

  /**
   * If the nav tree updates, then set any FLUSHING activities to FLUSHED
   * as this means the hot reload has updated to the latest flushed state
   */
  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    if (
      activities?.some((activity: any) => activity.flush_status === "FLUSHING")
    ) {
      updateFlushed();
    }
  }, [storyboardToNavTree, supabaseClient]);

  /**
   * If a flush started but hasn't finished in 4 seconds, mark it as complete anyway. This can happen
   * if the combination of changes doesn't trigger any hot reload
   */
  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    // @ts-expect-error TS2339
    if (new Date().getTime() - flushingStartedTs?.getTime() > 4000) {
      updateFlushed();
      setFlushingStartedTs(null);
    }
  }, [checkTrigger, supabaseClient]);

  useEffect(() => {
    if (!flushingStartedTs) {
      // @ts-expect-error TS2345
      setFlushingStartedTs(new Date());
    }
  }, [flushing]);

  /**
   * When the user navigates away from the tab (e.g. switches windows),
   * trigger a flush
   */
  useEffect(() => {
    const handleBlur = () => {
      setTriggerFlush(true);
    };

    window.addEventListener("blur", handleBlur);

    // Cleanup function
    return () => {
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // To prevent multiple calls at once
  const checkingIfFlushRef = useRef(false);

  /**
   * If there hasn't been a change in the last 3 seconds, trigger a flush.
   *
   */
  useEffect(() => {
    if (checkingIfFlushRef.current) {
      return;
    }

    checkingIfFlushRef.current = true;

    if (ongoingChange) {
      checkingIfFlushRef.current = false;
      return;
    }

    if (flushing) {
      checkingIfFlushRef.current = false;
      return;
    }

    const hasUnflushedActivities = activities?.some(
      (activity: any) => activity.flush_status === "AWAITING"
    );
    if (!hasUnflushedActivities) {
      setTriggerFlush(false);
      checkingIfFlushRef.current = false;
      return;
    }

    // Sometimes can be in the state where there are unflushed activities but the lastChangeTs is not set
    // make sure to trigger a flush in 3 seconds in that case
    if (!lastChangeTs && !triggerFlush) {
      // @ts-expect-error TS2345
      setLastChangeTs(new Date());
      checkingIfFlushRef.current = false;
      return;
    }

    const flushFunc = async () => {
      const supabaseJWT = await getUnexpiredToken(session);
      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/flushChanges`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      setLastChangeTs(null);
      setTriggerFlush(false);

      checkingIfFlushRef.current = false;
    };

    if (
      triggerFlush ||
      // @ts-expect-error TS18047
      new Date().getTime() - lastChangeTs.getTime() > flushDelay
    ) {
      flushFunc();
    } else {
      checkingIfFlushRef.current = false;
    }
  }, [lastChangeTs, activities, checkTrigger, triggerFlush]);

  // As soon as we show a toast instead of instant updating, we continue showing toasts
  // until all these changes are resolved through the API
  const blockInstantUpdates = useMemo(
    () =>
      Boolean(
        ledger.find(
          (item) =>
            item.needToProcessChange() && item.showedToastInsteadOfInstantUpdate
        )
      ),
    [ledger]
  );

  /**
   * Trigger instant updates for all the new ledger items
   */
  useEffect(() => {
    let itemsToSendInstantUpdate = ledger?.filter(
      (item: AnyChangeLedgerItem) => {
        return item.needsToSendInstantUpdate();
      }
    );

    // Loop through all the items that need to send an instant update and handle them accordingly
    itemsToSendInstantUpdate.forEach((item: AnyChangeLedgerItem) => {
      // Note - this may be null if it's not an undo or redo change
      const changeToUndoRedo =
        item.type === ChangeType.UNDO
          ? (item.changeFields as UndoChangeFields)?.changeToUndo
          : (item.changeFields as RedoChangeFields)?.changeToRedo;

      const showToastInsteadOfInstantUpdate =
        // If flushing, always show the toast instead of sending an instant update
        (flushing && !item.canInstantUpdateWhileFlushing) ||
        // If it's an undo/redo item without instant updates, also show the toast
        ((item.type === ChangeType.UNDO || item.type === ChangeType.REDO) &&
          !CHANGE_TYPES_WITH_INSTANT_UNDO.includes(changeToUndoRedo.type)) ||
        // If instant updates are being blocked right now, show the toast too
        blockInstantUpdates;

      if (showToastInsteadOfInstantUpdate) {
        item.markInstantUpdateSent();

        let displayName = "";

        if (changeToUndoRedo) {
          displayName = getDisplayNameForItem(changeToUndoRedo, activities);
        } else {
          displayName = getDisplayNameForItem(item, activities);
        }

        let toastMessage = displayName;
        if (item.type === ChangeType.UNDO) {
          toastMessage = toastMessage
            ? `Undo: ${toastMessage}`
            : `Undoing Change`;
        } else if (item.type === ChangeType.REDO) {
          toastMessage = toastMessage
            ? `Redo: ${toastMessage}`
            : `Redoing Change`;
        }

        const toastId = toast.loading(toastMessage);
        item.toastId = toastId;
        item.showedToastInsteadOfInstantUpdate = true;

        // As a backup - close the toast after 5 seconds
        setTimeout(() => {
          if (item.toastId) {
            toast.error("Failed", { id: item.toastId });
          }
        }, 5000);

        updateChange(item);
        return;
      }

      // This is needed so that the instant update knows if the change was flushed or not when undoing
      if (item.type === ChangeType.UNDO) {
        const changeToUndo = (item.changeFields as UndoChangeFields)
          .changeToUndo;
        const matchingActivity = activities.find(
          (activity) => activity.id === changeToUndo.activityId
        );
        item.changeFields.matchingActivityFlushed =
          matchingActivity?.flush_status === "FLUSHED";
        updateChange(item);
      }

      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          payload: {
            functionName: "applyChangeItemToDocument",
            args: [item],
          },
        })
      );

      // @ts-expect-error TS18048
      const ledgerAfterThisChange = ledger.slice(item.indexInLedger + 1);

      // If we are undoing or redoing, reapply the codebase Id changes to the rest of the ledger
      if (item.type === ChangeType.UNDO) {
        // Apply the undone prev Id to new Id map to the ledger
        const reverseMap = {};
        for (const [key, value] of Object.entries(
          (item as UndoChange)?.changeFields?.changeToUndo?.prevIdToNewIdMap ||
            {}
        )) {
          reverseMap[value] = key;
        }

        ledgerAfterThisChange.forEach((ledgerItem) => {
          ledgerItem.applyAllCodebaseIdChanges(reverseMap);
        });
      } else if (item.type === ChangeType.REDO) {
        // Reapply the original prev id to new id map
        ledgerAfterThisChange.forEach((ledgerItem) => {
          ledgerItem.applyAllCodebaseIdChanges(
            (item as RedoChange)?.changeFields?.changeToRedo
              ?.prevIdToNewIdMap || {}
          );
        });
      }

      item.markInstantUpdateSent();
      updateChange(item);
    });
  }, [ledger, flushing]);

  /**
   * If the ledger changes, pick out an uncomsumed change and apply it
   */
  useEffect(() => {
    // There is already a change being processed, return
    if (changeIsBeingProcessedRef.current) {
      return;
    }

    setInternalChangeBeingProcessed(true);

    if (!session) {
      setInternalChangeBeingProcessed(false);
      return;
    }

    // Early check if there is any uncomsumed change
    if (!ledger.length || !ledger[ledger.length - 1].needToProcessChange()) {
      setInternalChangeBeingProcessed(false);
      return;
    }

    const changeToProcess = ledger.find((change: AnyChangeLedgerItem) => {
      return change.needToProcessChange();
    });

    // Check if the activity ID matches for any UNDO/REDO events. If it doesn't
    // reset the ledger and add a new event on the end
    // Note: the reset ledger function re-attaches unconsumed undo/redo events to the right
    // activities, to make sure they can be undone
    // @ts-expect-error TS18048
    if (changeToProcess.type === ChangeType.UNDO) {
      const activityIdToUndo = // @ts-expect-error TS18048
        (changeToProcess.changeFields as UndoChangeFields).changeToUndo
          .activityId;

      const expectedActivity = activities.find(
        (activity) => !activity.change_undone
      );

      if (activityIdToUndo !== expectedActivity?.id) {
        setInternalChangeBeingProcessed(false);
        resetLedgerToActivityStream();

        // @ts-expect-error TS18048
        if (changeToProcess.toastId) {
          // @ts-expect-error TS18048
          toast.dismiss(changeToProcess.toastId);
        }
        return;
      }
      // @ts-expect-error TS18048
    } else if (changeToProcess.type === ChangeType.REDO) {
      const activityIdToRedo = // @ts-expect-error TS18048
        (changeToProcess.changeFields as RedoChangeFields).changeToRedo
          .activityId;

      const expectedActivity: any = Array.from(activities)
        .reverse()
        .find((activity: any) => activity.change_undone); // Oldest undone activity

      if (activityIdToRedo !== expectedActivity?.id) {
        setInternalChangeBeingProcessed(false);
        resetLedgerToActivityStream();

        // @ts-expect-error TS18048
        if (changeToProcess.toastId) {
          // @ts-expect-error TS18048
          toast.dismiss(changeToProcess.toastId);
        }
        return;
      }
    }

    // @ts-expect-error TS2345
    setLastChangeTs(new Date());

    const { urlPath, body, successToastMessage } =
      // @ts-expect-error TS18048
      changeToProcess.prepareApiRequest(
        canvasId,
        treeElementLookupRef.current,
        canvas
      );

    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(`${import.meta.env.VITE_BACKEND_BASE_URL}/${urlPath}`, body, {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        })
        .then((response: any) => {
          // Get the latest change directly from the state in case it changed
          const updatedChange =
            useChangeLedgerStore.getState().ledger[
              // @ts-expect-error TS18048
              changeToProcess.indexInLedger
            ] || changeToProcess;

          let newState;
          if (response?.data) {
            updateLookupsBasedOnPartialResponse(response.data);
            triggerRefetchActivityStream();

            // Refresh state
            newState = useCodebaseStore.getState();

            treeElementLookupRef.current = newState.treeElementLookup;
          }

          if (response?.data?.warning) {
            toast.warning(response.data.warning);
          } else if (successToastMessage) {
            toast.success(successToastMessage);
          }

          if (updatedChange.toastId) {
            toast.success("Success", { id: updatedChange.toastId });
            updatedChange.toastId = null;
          }

          // If there is info returned about codebase IDs that changed, update the iframe and the selected elements
          // Note - the update codebase IDs function inside devtools also updates the selected/hovered codebase IDs
          if (response?.data?.prevIdToNewIdMap) {
            // If the change was undone while the API was in flight, don't apply the new codebase ID changes
            if (!updatedChange.changeUndone) {
              const treeElementToSend = newState?.treeElementLookup
                ? getTreeElementLookupToSend(newState?.treeElementLookup)
                : undefined;

              dispatch(
                sendMessageToIFrame({
                  type: "executeFunction",
                  payload: {
                    functionName: "updateCodebaseIds",
                    args: [
                      response.data.prevIdToNewIdMap,
                      treeElementToSend,
                      newState?.scopeLookup,
                    ],
                  },
                })
              );

              const latestLedger = useChangeLedgerStore.getState().ledger;
              const ledgerAfterThisChange = latestLedger.slice(
                updatedChange.indexInLedger + 1
              );

              // Also apply the changed codebase IDs to the rest of the ledger
              ledgerAfterThisChange.forEach((ledgerItem) => {
                ledgerItem.applyAllCodebaseIdChanges(
                  response.data.prevIdToNewIdMap
                );
              });

              // TODO: Issue is that the tree elements are not updated before the next request is sent to the backend
            }

            updatedChange.prevIdToNewIdMap = response.data.prevIdToNewIdMap;
          }

          // This triggers selecting a particular codebase ID after changing
          if (response.data.selectCodebaseId && !updatedChange.changeUndone) {
            dispatch(
              setPendingSelectionCodebaseId(response.data.selectCodebaseId)
            );
          }

          if (response?.data?.activityId) {
            updatedChange.activityId = response.data.activityId;
          }
          updatedChange.markProcessedSucceeded();
          updateChange(updatedChange);

          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );

          dispatch(
            fetchFileTree({
              session,
              canvasId,
            })
          );

          updatedChange.resolveApi(response?.data);

          // If the instant update failed trigger a flush right after the activity is created
          if (!updatedChange.wasInstantUpdateSuccessful()) {
            setTriggerFlush(true);
          }

          dispatch(setIFrameChanged());
          setInternalChangeBeingProcessed(false);
        })
        .catch(async (error: any) => {
          // Get the latest change directly from the state in case it changed
          const updatedChange =
            useChangeLedgerStore.getState().ledger[
              // @ts-expect-error TS18048
              changeToProcess.indexInLedger
            ] || changeToProcess;

          // Mark it as failed
          updatedChange.markProcessedFailed();
          updateChange(updatedChange);

          setInternalChangeBeingProcessed(false);

          dispatch(forceReloadIFrame());
          dispatch(setIFrameChanged());

          if (updatedChange.toastId) {
            toast.error(
              "Failed to apply change: " + getAxiosErorrMessage(error),
              { id: updatedChange.toastId }
            );
            updatedChange.toastId = null;
          } else {
            toast.error(
              "Failed to apply change: " + getAxiosErorrMessage(error)
            );
          }

          updatedChange.rejectApi(error);

          // If there is an error it could be because the codebase is out of sync, automatically reload the codebase
          // Mark this as flushing even though it's technically not to show the syncing indicator and to prevent more changes
          await loadCodebase(true);

          if (error.response?.status === 401) {
            signOut({
              redirectUrl: window.location.origin,
            });
          }
        });
    });
  }, [ledger, changeBeingProcessed]);

  return <></>;
});

export default ChangeLedgerHelper;
